import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../context/authContext';
import { convertInquiry, ConvertInquiryPayload, listInquiries, setInquiryStatus } from '../../api/inquiries';
import { InquiryListItem, InquiryStatus } from '../../types/inquiry';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { ConvertInquiryDialog } from './ConvertInquiryDialog';

const STATUS_COLOR: Record<InquiryStatus, 'info' | 'success' | 'error' | 'default'> = {
  NEW: 'info',
  CONVERTED: 'success',
  SPAM: 'error',
  DISCARDED: 'default',
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};

export const InquiriesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [status, setStatus] = useState<InquiryStatus | ''>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<InquiryListItem | null>(null);
  const [convertTarget, setConvertTarget] = useState<InquiryListItem | null>(null);
  const [convertError, setConvertError] = useState<string | null>(null);

  const canConvert = hasPermission('inquiries', 'convert');
  const canManage = hasPermission('inquiries', 'manage');

  const inquiriesQuery = useQuery({
    queryKey: ['inquiries', paginationModel.page, paginationModel.pageSize, status, search],
    queryFn: () =>
      listInquiries({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        status: status || undefined,
        search: search || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['inquiries'] });

  const convertMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ConvertInquiryPayload }) => convertInquiry(id, payload),
    onSuccess: (lead) => {
      invalidate();
      setConvertTarget(null);
      setConvertError(null);
      setSelected(null);
      navigate(`/leads/${lead.id}`);
    },
    onError: (err) => setConvertError(extractErrorMessage(err)),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, next }: { id: number; next: 'SPAM' | 'DISCARDED' }) => setInquiryStatus(id, next),
    onSuccess: () => {
      invalidate();
      setSelected(null);
    },
  });

  const columns: GridColDef<InquiryListItem>[] = useMemo(
    () => [
      { field: 'inquiryNumber', headerName: 'Inquiry #', width: 130 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
      },
      { field: 'phone', headerName: 'Phone', width: 140 },
      {
        field: 'countryOfInterest',
        headerName: 'Country of interest',
        width: 170,
        valueGetter: (_v, row) => row.countryOfInterest ?? '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => <Chip size="small" label={params.value} color={STATUS_COLOR[params.value as InquiryStatus]} />,
      },
      {
        field: 'createdAt',
        headerName: 'Received',
        width: 160,
        valueGetter: (_v, row) => new Date(row.createdAt).toLocaleString(),
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={MarkEmailUnreadIcon}
        color="#7F56D9"
        title="Inquiries"
        subtitle="Review public form submissions before they become leads."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search name, phone, email, inquiry #…"
          size="small"
          sx={{ width: 300 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ width: 180 }}
          value={status}
          onChange={(e) => setStatus(e.target.value as InquiryStatus | '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="NEW">New</MenuItem>
          <MenuItem value="CONVERTED">Converted</MenuItem>
          <MenuItem value="SPAM">Spam</MenuItem>
          <MenuItem value="DISCARDED">Discarded</MenuItem>
        </TextField>
      </Stack>

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={inquiriesQuery.data?.data ?? []}
          columns={columns}
          rowCount={inquiriesQuery.data?.meta.total ?? 0}
          loading={inquiriesQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelected(params.row)}
        />
      </Box>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selected?.firstName} {selected?.lastName}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={1.5}>
              <Chip
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                label={selected.status}
                color={STATUS_COLOR[selected.status]}
              />
              <Typography variant="body2">
                {selected.inquiryNumber} · {selected.phone} {selected.email ? `· ${selected.email}` : ''}
              </Typography>
              {selected.countryOfInterest && (
                <Typography variant="body2">Country of interest: {selected.countryOfInterest}</Typography>
              )}
              {selected.courseOfInterest && (
                <Typography variant="body2">Course of interest: {selected.courseOfInterest}</Typography>
              )}
              {selected.message && (
                <Typography variant="body2" color="text.secondary">
                  "{selected.message}"
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Received {new Date(selected.createdAt).toLocaleString()}
                {selected.ipAddress ? ` from ${selected.ipAddress}` : ''}
              </Typography>
              {selected.convertedLead && (
                <Alert severity="success">
                  Converted to lead {selected.convertedLead.leadNumber}
                  {selected.convertedBy ? ` by ${selected.convertedBy.firstName} ${selected.convertedBy.lastName}` : ''}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {selected?.status === 'NEW' && canManage && (
            <>
              <Button color="error" onClick={() => statusMutation.mutate({ id: selected.id, next: 'SPAM' })}>
                Mark spam
              </Button>
              <Button onClick={() => statusMutation.mutate({ id: selected.id, next: 'DISCARDED' })}>Discard</Button>
            </>
          )}
          <Button onClick={() => setSelected(null)}>Close</Button>
          {selected?.status === 'NEW' && canConvert && (
            <Button
              variant="contained"
              onClick={() => {
                setConvertError(null);
                setConvertTarget(selected);
              }}
            >
              Convert to lead
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConvertInquiryDialog
        inquiry={convertTarget}
        errorMessage={convertError}
        onClose={() => setConvertTarget(null)}
        onSubmit={async (payload) => {
          if (convertTarget) {
            await convertMutation.mutateAsync({ id: convertTarget.id, payload });
          }
        }}
      />
    </Box>
  );
};
